// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { styles } from "@guestsupply/App/Widgets/HotspotBanner/HotspotBanner";
import { color, tippySharedStyles, xScreen } from "@guestsupply/XDL/Utilities";
import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

styles.widgetWrapper = {
    css: css`
        position: relative;
    `,
};

styles.slidesWrapper = {
    css: css`
        overflow: hidden;
        border-radius: 4px;
    `,
};
styles.carouselWrapper = {
    css: css`
        display: flex;
    `,
};
styles.slideImageAndContentWrapper = {
    css: css`
        display: flex;
        flex: 0 0 100%;
        position: relative;
        ${xScreen("lte", "md")} {
            flex-direction: column;
        }
    `,
};
styles.slideContentWrapper = {
    css: css`
        padding: 40px 24px;
        width: 50%;
        max-height: 500px;
        background: linear-gradient(
            to right,
            ${color("common.ui1")} 0%,
            ${color("common.ui1")} 25%,
            ${color("common.ui2")} 100%
        );
        ${xScreen("lte", "md")} {
            order: 2;
            padding: 28px 16px;
            width: 100%;
            height: 100%;
            max-height: unset;
            background: ${getColor("common.ui2")};
        }
    `,
};
styles.slideTitle = {
    as: "p",
    size: 36,
    weight: "bold",
    lineHeight: "39px",
    css: css`
        letter-spacing: 0;
        margin-bottom: 16px;
        ${xScreen("lte", "sm")} {
            line-height: 30px;
            font-size: 28px;
        }
    `,
};
styles.slideSubTitle = {
    as: "p",
    variant: "h4",
    css: css`
        &,
        ${TypographyStyle} {
            margin-bottom: 0;
            > p {
                font-size: 20px;
                font-weight: bold;
            }
        }
    `,
};
styles.slideButton = {
    sizeVariant: "large",
    css: css`
        margin-top: 20px;
    `,
};
styles.slideImageContainer = {
    css: css`
        position: relative;
        width: 50%;
        height: 100%;
        max-height: 500px;

        ${xScreen("lte", "md")} {
            width: 100%;
            height: auto;
            max-height: unset;
        }
    `,
};
styles.image = {
    css: css`
        width: 100%;
        height: 100%;
        img {
            height: auto;
            width: 100%;
            object-fit: unset;
            aspect-ratio: 667 / 500;
        }
        ${xScreen("lte", "md")} {
            width: 100%;
            height: 100%;
            max-height: 270px;
            img {
                aspect-ratio: auto;
            }
        }
    `,
};
styles.tippyPinWrapper = {
    ...tippySharedStyles,
};

styles.point = {
    css: css`
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        cursor: pointer;
        top: ${(props: any) => `calc(${props.yPercentage}% - 20px)`};
        left: ${(props: any) => `calc(${props.xPercentage}% - 20px)`};
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background: ${color("common.ui7", 0.33)};

        &:hover {
            background: ${color("common.ui9", 0.33)};
        }
        &&&.selected {
            z-index: 88888;
            background: ${color("common.ui9", 0.33)};
        }
    `,
};
styles.innerPoint = {
    css: css`
        width: 25px;
        height: 25px;
        cursor: pointer;
        background: ${(props: any) => props.pointColor};
        border-radius: 50%;
        border: 1px solid ${color("common.ui1")};
        position: absolute;
        top: 50%;
        left: 50%;
        translate: -50% -50%;
    `,
};
styles.dotsContainerWrapper = {
    css: css`
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 6px;
        margin-top: 8px;
    `,
};

styles.notify = {
    css: css`
        margin-bottom: 0;
    `,
};

const sharedArrowWrapper: InjectableCss = {
    css: css`
        position: absolute;
        z-index: 1;
        top: 50%;
        transform: translate(0, -50%);
    `,
};

styles.prevArrowWrapper = {
    ...sharedArrowWrapper,
};

styles.nextArrowWrapper = {
    css: css`
        ${sharedArrowWrapper.css}
        right: 0;
    `,
};

styles.prevArrowButton = {
    css: css`
        background: ${color("common.ui6")};
        opacity: 0.66;
        border: 0;
        border-radius: 0;
        height: 66px;
        padding: 20px 8px;

        & > ${IconWrapper} {
            color: ${color("common.white")};
            &,
            & > svg {
                height: 26px;
            }
        }

        ${xScreen("lte", "sm")} {
            display: none;
        }
    `,
};

styles.nextArrowButton = {
    css: css`
        background: ${color("common.ui6")};
        opacity: 0.66;
        border: 0;
        border-radius: 0;
        height: 66px;
        padding: 20px 8px;

        & > ${IconWrapper} {
            color: ${color("common.white")};
            &,
            & > svg {
                height: 26px;
            }
        }

        ${xScreen("lte", "sm")} {
            display: none;
        }
    `,
};

styles.iconProps = {
    size: 16,
};

styles.dotButton = {
    css: css`
        flex: 1 1 0;
        min-height: auto;
        height: 6px;
        border: 0;
        border-radius: 0;
        background: ${getColor("common.ui5")};
        &:hover,
        &:active {
            background: ${getColor("common.ui8")};
        }
    `,
};

styles.pauseIcon = {
    size: 12,
    color: "primary",
    css: css`
        &:hover {
            svg {
                color: ${color("common.ui8")};
            }
        }
    `,
};
